import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { AppProvider, useApp } from './components/AppContext';
import ErrorBoundary from './components/ErrorBoundary';
import { YandexRTB } from './components/YandexRTB';

// Lazy load all screen components for better performance
const MainScreen = lazy(() => import('./components/MainScreen').then(m => ({ default: m.MainScreen })));
const PreparationScreen = lazy(() => import('./components/PreparationScreen').then(m => ({ default: m.PreparationScreen })));
const PreparationHelpScreen = lazy(() => import('./components/PreparationHelpScreen').then(m => ({ default: m.PreparationHelpScreen })));
const ExamScreen = lazy(() => import('./components/ExamScreen').then(m => ({ default: m.ExamScreen })));
const ExamHelpScreen = lazy(() => import('./components/ExamHelpScreen').then(m => ({ default: m.ExamHelpScreen })));
const MarathonScreen = lazy(() => import('./components/MarathonScreen').then(m => ({ default: m.MarathonScreen })));
const MarathonHelpScreen = lazy(() => import('./components/MarathonHelpScreen').then(m => ({ default: m.MarathonHelpScreen })));
const FavoritesScreen = lazy(() => import('./components/FavoritesScreen').then(m => ({ default: m.FavoritesScreen })));
const FavoritesHelpScreen = lazy(() => import('./components/FavoritesHelpScreen').then(m => ({ default: m.FavoritesHelpScreen })));
const SettingsScreen = lazy(() => import('./components/SettingsScreen').then(m => ({ default: m.SettingsScreen })));
const TestScreen = lazy(() => import('./components/TestScreen').then(m => ({ default: m.TestScreen })));
const ResultsScreen = lazy(() => import('./components/ResultsScreen').then(m => ({ default: m.ResultsScreen })));
const AnswerReviewScreen = lazy(() => import('./components/AnswerReviewScreen').then(m => ({ default: m.AnswerReviewScreen })));
const StatisticsScreen = lazy(() => import('./components/StatisticsScreen').then(m => ({ default: m.StatisticsScreen })));
const StatisticsHelpScreen = lazy(() => import('./components/StatisticsHelpScreen').then(m => ({ default: m.StatisticsHelpScreen })));
const ErrorTopScreen = lazy(() => import('./components/ErrorTopScreen').then(m => ({ default: m.ErrorTopScreen })));
const ErrorTopHelpScreen = lazy(() => import('./components/ErrorTopHelpScreen').then(m => ({ default: m.ErrorTopHelpScreen })));
const UsefulInfoScreen = lazy(() => import('./components/UsefulInfoScreen').then(m => ({ default: m.UsefulInfoScreen })));
const UsefulLinksScreen = lazy(() => import('./components/UsefulLinksScreen').then(m => ({ default: m.UsefulLinksScreen })));
const DocumentViewScreen = lazy(() => import('./components/DocumentViewScreen').then(m => ({ default: m.DocumentViewScreen })));
const DocumentHelpScreen = lazy(() => import('./components/DocumentHelpScreen').then(m => ({ default: m.DocumentHelpScreen })));
const SearchScreen = lazy(() => import('./components/SearchScreen').then(m => ({ default: m.SearchScreen })));
const PrivacyScreen = lazy(() => import('./components/PrivacyScreen').then(m => ({ default: m.PrivacyScreen })));
const TermsScreen = lazy(() => import('./components/TermsScreen').then(m => ({ default: m.TermsScreen })));
const ContactsScreen = lazy(() => import('./components/ContactsScreen').then(m => ({ default: m.ContactsScreen })));
const AboutScreen = lazy(() => import('./components/AboutScreen').then(m => ({ default: m.AboutScreen })));
const CookieConsent = lazy(() => import('./components/CookieConsent').then(m => ({ default: m.default })));
const UpdateNotice = lazy(() => import('./components/UpdateNotice').then(m => ({ default: m.default })));
const Error404Screen = lazy(() => import('./components/Error404Screen').then(m => ({ default: m.Error404Screen })));
// SEO Document Pages
const DocumentsCatalogPage = lazy(() => import('./components/DocumentsCatalogPage').then(m => ({ default: m.DocumentsCatalogPage })));
const DocumentDetailPage = lazy(() => import('./components/DocumentDetailPage').then(m => ({ default: m.DocumentDetailPage })));
const LinksPage = lazy(() => import('./components/LinksPage').then(m => ({ default: m.LinksPage })));

export type Screen = 'main' | 'preparation' | 'exam' | 'marathon' | 'favorites' | 'settings' | 'test' | 'results' | 'review' | 'statistics' | 'error-top' | 'useful-info' | 'useful-links' | 'document-view' | 'search' | 'preparation-help' | 'exam-help' | 'marathon-help' | 'favorites-help' | 'statistics-help' | 'error-top-help' | 'document-help' | 'privacy' | 'terms' | 'contacts' | 'about' | '404' | 'documents-catalog' | 'doc-77fz' | 'doc-weapons' | 'doc-periodic-check' | 'doc-criminal-code' | 'useful-links-page';

const AppInner: React.FC = () => {
  // Восстанавливаем состояние экрана из URL path или sessionStorage
  const getInitialScreen = (): Screen => {
    // Сначала проверяем URL path
    const path = window.location.pathname;
    if (path) {
      // Маппинг URL path к Screen
      const pathToScreen: Record<string, Screen> = {
        '/about': 'about',
        '/contacts': 'contacts',
        '/privacy': 'privacy',
        '/terms': 'terms',
        '/documents': 'documents-catalog',
        '/documents/77-fz': 'doc-77fz',
        '/documents/weapons-law': 'doc-weapons',
        '/documents/periodic-check': 'doc-periodic-check',
        '/documents/criminal-code': 'doc-criminal-code',
        '/links': 'useful-links-page',
        '/preparation': 'preparation',
        '/exam': 'exam',
        '/marathon': 'marathon',
        '/favorites': 'favorites',
        '/statistics': 'statistics',
        '/error-top': 'error-top',
        '/search': 'search',
        '/settings': 'settings',
        '/': 'main',
      };

      const screen = pathToScreen[path];
      if (screen) {
        return screen;
      }

      // Если путь есть, но он не в списке - это 404
      if (path !== '/') {
        return '404';
      }
    }

    // Если hash не распознан, читаем из sessionStorage
    const savedScreen = sessionStorage.getItem('currentScreen') as Screen;
    return savedScreen || 'main';
  };

  const getInitialScreenStack = (): Screen[] => {
    const savedStack = sessionStorage.getItem('screenStack');
    return savedStack ? JSON.parse(savedStack) : [];
  };

  const [currentScreen, setCurrentScreen] = useState<Screen>(getInitialScreen);
  const [screenStack, setScreenStack] = useState<Screen[]>(getInitialScreenStack);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const { getBackgroundClass } = useApp();

  // Защита от двойного показа fullscreen interstitial (не чаще 60с)
  const lastInterstitialRef = useRef(0);
  // Актуальный экран для отложенного показа: за 500мс юзер может уйти с results
  const currentScreenRef = useRef<Screen>(currentScreen);
  currentScreenRef.current = currentScreen;
  const showInterstitialIfNeeded = (screen: Screen) => {
    if (screen !== 'results' || window.innerWidth >= 1024) return;
    const now = Date.now();
    if (now - lastInterstitialRef.current < 60_000) return;

    setTimeout(() => {
      // Юзер ушёл с results за время задержки — запрос не нужен (холостой hit)
      if (currentScreenRef.current !== 'results') return;
      lastInterstitialRef.current = Date.now();
      if ((window as any).yaContextCb) {
        try {
          (window as any).yaContextCb.push(() => {
            if ((window as any).Ya?.Context?.AdvManager) {
              (window as any).Ya.Context.AdvManager.render({
                blockId: "R-A-17579918-6",
                type: "fullscreen",
                platform: "touch"
              });
            }
          });
        } catch (e) {
          console.error("Failed to show interstitial", e);
        }
      }
    }, 500);
  };

  // Функция для сохранения состояния экрана в sessionStorage
  const saveScreenState = (screen: Screen, stack: Screen[]) => {
    sessionStorage.setItem('currentScreen', screen);
    sessionStorage.setItem('screenStack', JSON.stringify(stack));
  };

  // Синхронизация URL path с текущим экраном
  useEffect(() => {
    const screenToPath: Record<Screen, string> = {
      'about': '/about',
      'contacts': '/contacts',
      'privacy': '/privacy',
      'terms': '/terms',
      'documents-catalog': '/documents',
      'doc-77fz': '/documents/77-fz',
      'doc-weapons': '/documents/weapons-law',
      'doc-periodic-check': '/documents/periodic-check',
      'doc-criminal-code': '/documents/criminal-code',
      'useful-links-page': '/links',
      'main': '/',
      'preparation': '/preparation',
      'exam': '/exam',
      'marathon': '/marathon',
      'favorites': '/favorites',
      'settings': '/settings',
      'test': '/test',
      'results': '/results',
      'review': '/review',
      'statistics': '/statistics',
      'error-top': '/error-top',
      'useful-info': '/useful-info',
      'useful-links': '/useful-links',
      'document-view': '/document-view',
      'search': '/search',
      'preparation-help': '/preparation-help',
      'exam-help': '/exam-help',
      'marathon-help': '/marathon-help',
      'favorites-help': '/favorites-help',
      'statistics-help': '/statistics-help',
      'error-top-help': '/error-top-help',
      'document-help': '/document-help',
      '404': '/404',
    };

    const path = screenToPath[currentScreen] || '/';

    // Обновляем URL только если он изменился
    if (window.location.pathname !== path) {
      window.history.replaceState(null, '', path);
    }

    // Уведомляем Яндекс.Метрику о переходе при КАЖДОЙ смене экрана.
    // ВАЖНО: вызывается вне if-блока, иначе для экранов с одинаковым path='/'
    // hit никогда не срабатывал бы.
    // Используем реальный path-URL (History API), а не hash
    // replaceState выше уже обновил window.location.pathname
    const virtualUrl = window.location.href;
    if (typeof window.ym === 'function') {
      window.ym(104850457, 'hit', virtualUrl, { title: currentScreen });
    }
  }, [currentScreen]);

  // Динамическое обновление canonical URL для SEO
  useEffect(() => {
    const screenToCanonical: Record<Screen, string> = {
      'about': 'https://vohr-test.ru/about',
      'contacts': 'https://vohr-test.ru/contacts',
      'privacy': 'https://vohr-test.ru/privacy',
      'terms': 'https://vohr-test.ru/terms',
      'documents-catalog': 'https://vohr-test.ru/documents',
      'doc-77fz': 'https://vohr-test.ru/documents/77-fz',
      'doc-weapons': 'https://vohr-test.ru/documents/weapons-law',
      'doc-periodic-check': 'https://vohr-test.ru/documents/periodic-check',
      'doc-criminal-code': 'https://vohr-test.ru/documents/criminal-code',
      'useful-links-page': 'https://vohr-test.ru/links',
      'main': 'https://vohr-test.ru/',
      'preparation': 'https://vohr-test.ru/preparation',
      'exam': 'https://vohr-test.ru/exam',
      'marathon': 'https://vohr-test.ru/marathon',
      'favorites': 'https://vohr-test.ru/favorites',
      'settings': 'https://vohr-test.ru/settings',
      'test': 'https://vohr-test.ru/test',
      'results': 'https://vohr-test.ru/results',
      'review': 'https://vohr-test.ru/review',
      'statistics': 'https://vohr-test.ru/statistics',
      'error-top': 'https://vohr-test.ru/error-top',
      'useful-info': 'https://vohr-test.ru/useful-info',
      'useful-links': 'https://vohr-test.ru/useful-links',
      'document-view': 'https://vohr-test.ru/document-view',
      'search': 'https://vohr-test.ru/search',
      'preparation-help': 'https://vohr-test.ru/preparation-help',
      'exam-help': 'https://vohr-test.ru/exam-help',
      'marathon-help': 'https://vohr-test.ru/marathon-help',
      'favorites-help': 'https://vohr-test.ru/favorites-help',
      'statistics-help': 'https://vohr-test.ru/statistics-help',
      'error-top-help': 'https://vohr-test.ru/error-top-help',
      'document-help': 'https://vohr-test.ru/document-help',
    };
    // 404 не должен иметь canonical — удаляем тег и ставим noindex
    if (currentScreen === '404') {
      const canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (canonicalLink) canonicalLink.remove();

      let robotsMeta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
      if (!robotsMeta) {
        robotsMeta = document.createElement('meta');
        robotsMeta.name = 'robots';
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.content = 'noindex';
      return;
    }

    // Внутренние экраны приложения — не должны индексироваться поисковиками
    // (нет самостоятельного SEO-контента, Яндекс жалуется на пустые description)
    const noIndexScreens: Screen[] = [
      'test', 'results', 'review', 'settings',
      'useful-info', 'useful-links', 'document-view',
      'preparation-help', 'exam-help', 'marathon-help',
      'favorites-help', 'statistics-help', 'error-top-help', 'document-help',
    ];

    if (noIndexScreens.includes(currentScreen)) {
      let robotsMeta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
      if (!robotsMeta) {
        robotsMeta = document.createElement('meta');
        robotsMeta.name = 'robots';
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.content = 'noindex, nofollow';

      // Canonical не нужен для noindex-страниц
      const canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (canonicalLink) canonicalLink.remove();
      return;
    }

    // Для всех остальных экранов: убираем noindex (если был) и ставим canonical
    const robotsMeta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (robotsMeta) {
      // Восстанавливаем стандартные robots (index, follow)
      robotsMeta.content = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
    }

    const canonicalUrl = screenToCanonical[currentScreen] || 'https://vohr-test.ru/';

    // Найти или создать canonical link тег
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;

    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }

    canonicalLink.href = canonicalUrl;
  }, [currentScreen]);

  // ДИНАМИЧЕСКИЕ SEO ЗАГОЛОВКИ И ОПИСАНИЯ
  useEffect(() => {
    // Дефолтные значения (как в index.html)
    const defaultTitle = 'Билеты ведомственной охраны 2026 с ответами | ВОХР ТЕСТ';
    const defaultDesc = 'Бесплатные билеты ведомственной охраны 2026 с ответами. 235 вопросов, актуальные с 01.09.2026 (приказ №178). Тесты ВОХР онлайн, экзамен, с оружием — без регистрации.';

    const screenToSEO: Record<Screen, { title: string; description: string }> = {
      'about': {
        title: 'О проекте - ВОХР ТЕСТ | Подготовка к периодической проверке ведомственной охраны',
        description: 'Узнайте больше о платформе ВОХР ТЕСТ. Наша миссия — помочь сотрудникам ведомственной охраны бесплатно и эффективно подготовиться к сдаче периодической проверки.'
      },
      'contacts': {
        title: 'Контакты - ВОХР ТЕСТ | Служба поддержки',
        description: 'Контакты службы поддержки проекта ВОХР ТЕСТ. Свяжитесь с нами через Telegram или электронную почту для решения любых вопросов по тестированию ведомственной охраны.'
      },
      'privacy': {
        title: 'Политика конфиденциальности - ВОХР ТЕСТ',
        description: 'Политика конфиденциальности сервиса ВОХР ТЕСТ. Узнайте, как мы бережно собираем, используем и защищаем вашу личную информацию при использовании нашей образовательной платформы.'
      },
      'terms': {
        title: 'Пользовательское соглашение - ВОХР ТЕСТ',
        description: 'Ознакомьтесь с условиями пользовательского соглашения ВОХР ТЕСТ: правила использования тестов ведомственной охраны, права и обязанности сторон.'
      },
      'documents-catalog': {
        title: 'Периодическая проверка ВОХР и документы для ведомственной охраны | ВОХР-Тест',
        description: 'Законы и нормативные документы для подготовки к экзамену ведомственной охраны: периодическая проверка ВОХР, ФЗ-77, ФЗ об оружии, приказы Росгвардии и билеты с оружием.'
      },
      'doc-77fz': {
        title: 'ФЗ-77 «О ведомственной охране» — Полный текст | ВОХР-Тест',
        description: 'Федеральный закон №77-ФЗ от 14.04.1999 «О ведомственной охране». Права и обязанности охранника, применение оружия и спецсредств.'
      },
      'doc-weapons': {
        title: 'Билеты ведомственной охраны с оружием — ФЗ «Об оружии» | ВОХР-Тест',
        description: 'Федеральный закон «Об оружии» №150-ФЗ для подготовки ВОХР: хранение, ношение и применение служебного оружия, вопросы для билетов ведомственной охраны с оружием.'
      },
      'doc-periodic-check': {
        title: 'Периодическая проверка ведомственной охраны онлайн — Приказ №178 | ВОХР-Тест',
        description: 'Порядок периодической проверки ведомственной охраны: пригодность к применению оружия, упражнения по стрельбе, спецсредства и онлайн-подготовка к тесту ВОХР.'
      },
      'doc-criminal-code': {
        title: 'Статьи УК РФ для охранников ВОХР — Необходимая оборона | ВОХР-Тест',
        description: 'Уголовная ответственность охранника: необходимая оборона, крайняя необходимость, превышение полномочий.'
      },
      'useful-links-page': {
        title: 'Полезные ссылки для охранников ВОХР — Официальные ресурсы | ВОХР-Тест',
        description: 'Подборка полезных ссылок для ведомственной охраны: Росгвардия, порталы трудоустройства, учебные центры, нормативные документы.'
      },
      '404': {
        title: 'Страница не найдена (Ошибка 404) - ВОХР ТЕСТ',
        description: 'К сожалению, запрашиваемая страница не найдена на сайте ВОХР ТЕСТ. Вернитесь на главную, чтобы продолжить подготовку к периодической проверке ВОХР.'
      },
      // Для остальных экранов используем дефолтные значения или можно добавить более узкие
      'main': { title: defaultTitle, description: defaultDesc },
      'preparation': { title: 'Тесты ВОХР 2026 и билеты ведомственной охраны | Подготовка онлайн', description: 'Режим подготовки к экзамену ВОХР: изучайте актуальные билеты ведомственной охраны 2026, тесты с оружием, вопросы периодической проверки и ответы с подсказками.' },
      'exam': { title: 'Экзамен ВОХР онлайн 2026 - Симулятор периодической проверки | ВОХР ТЕСТ', description: 'Пройдите онлайн-экзамен ведомственной охраны 2026. Условия симулируют реальную периодическую проверку: ограниченное время, билеты со случайными вопросами и строгая оценка.' },
      'marathon': { title: 'Марафон: все 235 вопросов ведомственной охраны - ВОХР ТЕСТ', description: 'Марафон ВОХР ТЕСТ: ответьте на все 235 вопросов (актуальные билеты) подряд без права на ошибку. Идеальная тренировка на выносливость перед периодической проверкой ведомственной охраны.' },
      'favorites': { title: 'Избранные вопросы и ошибки - Тесты ВОХР | ВОХР ТЕСТ', description: 'Ваши сохранённые и сложные вопросы для целенаправленного повторения. Анализируйте свои ошибки в тестах ведомственной охраны для идеальной подготовки.' },
      'settings': { title: 'Настройки приложения - ВОХР ТЕСТ', description: 'Настройки образовательной платформы ВОХР ТЕСТ: управление звуком, вибрацией, данными и визуальным оформлением приложения.' },
      'test': { title: 'Прохождение тестирования ВОХР - ВОХР ТЕСТ', description: 'Тестирование ведомственной охраны онлайн: отвечайте на вопросы билетов, проверяйте свои знания по огневой, тактической и медицинской подготовке.' },
      'results': { title: 'Результаты тестирования ВОХР | ВОХР ТЕСТ', description: 'Ознакомьтесь с результатами вашего тестирования ВОХР: количество правильных ответов, затраченное время и прохождение порога периодической проверки.' },
      'review': { title: 'Разбор ошибок тестирования ВОХР | ВОХР ТЕСТ', description: 'Детальный разбор ошибок, допущенных в тесте ведомственной охраны. Узнайте правильные ответы с опорой на приказы Росгвардии и федеральные законы.' },
      'statistics': { title: 'Статистика подготовки ВОХР - Ваш прогресс | ВОХР ТЕСТ', description: 'Аналитика и статистика подготовки к периодической проверке: количество пройденных тестов, процент правильных ответов и прогресс по категориям ВОХР.' },
      'error-top': { title: 'Топ ошибок охранников - Самые частые ошибки ВОХР | ВОХР ТЕСТ', description: 'Подборка самых частых ошибок, которые допускают охранники ВОХР при сдаче периодической проверки. Изучите слабые места для успешной сдачи экзамена.' },
      'useful-info': { title: 'Полезная информация для охранника - ВОХР ТЕСТ', description: 'Информационная база для подготовки охранников ВОХР: каталоги нормативов, правовые статьи и учебные материалы.' },
      'useful-links': { title: 'Внешние ресурсы и сервисы для ВОХР - ВОХР ТЕСТ', description: 'Полезные ссылки и партнерские ресурсы для сотрудников ведомственной охраны.' },
      'document-view': { title: 'Просмотр документа - ВОХР ТЕСТ', description: 'Чтение и анализ правовых актов, законов и приказов, необходимых для сдачи тестов ведомственной охраны.' },
      'search': { title: 'Поиск по билетам ведомственной охраны 2026 и тестам ВОХР', description: 'Удобный поиск по базе: 235 актуальных вопросов и билетов ведомственной охраны 2026. Найдите тесты ВОХР, вопросы с оружием и темы периодической проверки по ключевому слову.' },
      'preparation-help': { title: 'Справка: Режим подготовки - ВОХР ТЕСТ', description: 'Помощь и инструкции по использованию режима подготовки к экзаменам ведомственной охраны.' },
      'exam-help': { title: 'Справка: Режим экзамена - ВОХР ТЕСТ', description: 'Узнайте правила и особенности прохождения симуляции периодического экзамена ВОХР.' },
      'marathon-help': { title: 'Справка: Марафон - ВОХР ТЕСТ', description: 'Как проходить Марафон ВОХР и дойти до самого конца из 235 вопросов ведомственной охраны.' },
      'favorites-help': { title: 'Справка: Избранные вопросы - ВОХР ТЕСТ', description: 'Инструкция по работе с избранными и сложными вопросами из базы тестов ведомственной охраны.' },
      'statistics-help': { title: 'Справка: Статистика - ВОХР ТЕСТ', description: 'Как правильно анализировать графики и данные вашей статистики подготовки ВОХР.' },
      'error-top-help': { title: 'Справка: Топ ошибок - ВОХР ТЕСТ', description: 'Помощь по использованию раздела самых частых ошибок тестирования ВОХР.' },
      'document-help': { title: 'Справка: Документы - ВОХР ТЕСТ', description: 'Как пользоваться каталогом нормативных актов и законов для ведомственной охраны на платформе ВОХР ТЕСТ.' }
    };

    const currentSEO = screenToSEO[currentScreen] || screenToSEO['main'];

    // Устанавливаем заголовок
    document.title = currentSEO.title;

    // Устанавливаем мета-описание
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', currentSEO.description);

    // Устанавливаем Open Graph
    const setMetaTag = (selector: string, attribute: string, content: string) => {
      let tag = document.querySelector(selector);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attribute.split('=')[0], attribute.split('=')[1].replace(/"/g, ''));
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    setMetaTag('meta[property="og:title"]', 'property="og:title"', currentSEO.title);
    setMetaTag('meta[property="og:description"]', 'property="og:description"', currentSEO.description);

    // canonicalUrl already determined above, but we need it here, so lets get it from the link tag we just created
    const canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    const url = canonicalLink ? canonicalLink.href : 'https://vohr-test.ru/';
    setMetaTag('meta[property="og:url"]', 'property="og:url"', url);

    // Устанавливаем Twitter Card
    setMetaTag('meta[name="twitter:title"]', 'name="twitter:title"', currentSEO.title);
    setMetaTag('meta[name="twitter:description"]', 'name="twitter:description"', currentSEO.description);
    setMetaTag('meta[name="twitter:url"]', 'name="twitter:url"', url);

  }, [currentScreen]);

  useEffect(() => {
    // Обработчик кнопки "Назад" браузера
    const handlePopState = () => {
      const path = window.location.pathname;
      const pathToScreen: Record<string, Screen> = {
        '/about': 'about',
        '/contacts': 'contacts',
        '/privacy': 'privacy',
        '/terms': 'terms',
        '/documents': 'documents-catalog',
        '/documents/77-fz': 'doc-77fz',
        '/documents/weapons-law': 'doc-weapons',
        '/documents/periodic-check': 'doc-periodic-check',
        '/documents/criminal-code': 'doc-criminal-code',
        '/links': 'useful-links-page',
        '/preparation': 'preparation',
        '/exam': 'exam',
        '/marathon': 'marathon',
        '/favorites': 'favorites',
        '/statistics': 'statistics',
        '/error-top': 'error-top',
        '/search': 'search',
        '/settings': 'settings',
        '/': 'main',
      };

      const screen = pathToScreen[path] || (path !== '/' ? '404' : 'main');
      if (screen !== currentScreen) {
        setCurrentScreen(screen);
      }
    };

    window.addEventListener('popstate', handlePopState);

    // Очистка обработчика при размонтировании компонента
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [currentScreen]);

  // iOS-fix: Окончательное решение проблемы блокировки скролла
  useEffect(() => {
    // Определяем touch-устройство (охватывает iPhone/iPad в любом режиме)
    const isTouchDevice = navigator.maxTouchPoints > 0 || 'ontouchstart' in window;
    if (!isTouchDevice) return;

    const unlock = () => {

      const stylesToRemove = ['overflow', 'overflow-x', 'overflow-y', 'position', 'top', 'touch-action'];
      let changed = false;

      [document.body, document.documentElement].forEach(el => {
        stylesToRemove.forEach(prop => {
          if (el.style.getPropertyValue(prop)) {
            el.style.removeProperty(prop);
            changed = true;
          }
        });
      });

      if (changed) {
        console.log('Scroll guard: unlocked body/html');
      }
    };

    // Следим и за style, и за class
    const observer = new MutationObserver((mutations) => {
      unlock();
    });

    const observerOptions = {
      attributes: true,
      attributeFilter: ['style', 'class']
    };

    observer.observe(document.body, observerOptions);
    observer.observe(document.documentElement, observerOptions);

    // Первичная очистка
    unlock();

    return () => observer.disconnect();
  }, []);

  const navigateTo = (screen: Screen) => {
    const fromScreen = currentScreen;
    const toScreen = screen;
    const stackLen = screenStack.length;
    console.log('App.navigateTo called: from=' + fromScreen + ', to=' + toScreen + ', stackLength=' + stackLen);

    showInterstitialIfNeeded(screen);

    setIsTransitioning(true);
    setTimeout(() => {
      const newStack = [...screenStack, currentScreen];
      setScreenStack(newStack);
      setCurrentScreen(screen);

      // Сохраняем состояние экрана
      saveScreenState(screen, newStack);

      setTimeout(() => {
        // Сброс скролла на всех уровнях
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;

        const scrollableContainer = document.querySelector('.app-content-scrollable');
        if (scrollableContainer) {
          scrollableContainer.scrollTop = 0;
        }

        // Сброс всех скроллируемых элементов
        document.querySelectorAll('.overflow-y-auto, .app-content-scrollable').forEach(el => {
          (el as HTMLElement).scrollTop = 0;
        });
      }, 50);

      setIsTransitioning(false);
    }, 75);
  };

  const goBack = () => {
    console.log('⬅️ App.goBack called', {
      currentScreen,
      stackLength: screenStack.length,
      stack: [...screenStack],
      caller: new Error().stack
    });
    setIsTransitioning(true);
    setTimeout(() => {
      let newScreen: Screen;
      let newStack: Screen[];

      if (screenStack.length > 0) {
        const previousScreen = screenStack[screenStack.length - 1];
        console.log('⬅️ Going back to:', previousScreen);
        newScreen = previousScreen;
        newStack = screenStack.slice(0, -1);
      } else {
        console.log('⬅️ Stack empty, going to main');
        newScreen = 'main';
        newStack = [];
      }

      setCurrentScreen(newScreen);
      setScreenStack(newStack);

      // Сохраняем состояние экрана
      saveScreenState(newScreen, newStack);

      setTimeout(() => {
        // Сброс скролла на всех уровнях
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;

        const scrollableContainer = document.querySelector('.app-content-scrollable');
        if (scrollableContainer) {
          scrollableContainer.scrollTop = 0;
        }

        // Сброс всех скроллируемых элементов
        document.querySelectorAll('.overflow-y-auto, .app-content-scrollable').forEach(el => {
          (el as HTMLElement).scrollTop = 0;
        });
      }, 50);

      setIsTransitioning(false);
    }, 75);
  };

  const goToMain = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentScreen('main');
      setScreenStack([]);

      // Сохраняем состояние экрана
      saveScreenState('main', []);

      setTimeout(() => {
        // Сброс скролла на всех уровнях
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;

        const scrollableContainer = document.querySelector('.app-content-scrollable');
        if (scrollableContainer) {
          scrollableContainer.scrollTop = 0;
        }

        // Сброс всех скроллируемых элементов
        document.querySelectorAll('.overflow-y-auto, .app-content-scrollable').forEach(el => {
          (el as HTMLElement).scrollTop = 0;
        });
      }, 50);

      setIsTransitioning(false);
    }, 75);
  };

  const replaceWithScreen = (screen: Screen) => {
    setIsTransitioning(true);

    showInterstitialIfNeeded(screen);

    setTimeout(() => {
      setCurrentScreen(screen);

      // Сохраняем состояние экрана (стек остается тем же)
      saveScreenState(screen, screenStack);

      setTimeout(() => {
        // Сброс скролла на всех уровнях
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;

        const scrollableContainer = document.querySelector('.app-content-scrollable');
        if (scrollableContainer) {
          scrollableContainer.scrollTop = 0;
        }

        // Сброс всех скроллируемых элементов
        document.querySelectorAll('.overflow-y-auto, .app-content-scrollable').forEach(el => {
          (el as HTMLElement).scrollTop = 0;
        });
      }, 50);

      setIsTransitioning(false);
    }, 75);
  };

  const renderScreen = () => {
    switch (currentScreen) {
      case 'main':
        return <MainScreen onNavigate={navigateTo} />;
      case 'preparation':
        return <PreparationScreen onNavigate={navigateTo} onBack={goBack} />;
      case 'exam':
        return <ExamScreen onNavigate={navigateTo} onBack={goBack} />;
      case 'marathon':
        return <MarathonScreen onNavigate={navigateTo} onBack={goBack} />;
      case 'favorites':
        return <FavoritesScreen onNavigate={navigateTo} onBack={goBack} />;
      case 'settings':
        return <SettingsScreen onBack={goBack} />;
      case 'test':
        return <TestScreen onNavigate={navigateTo} onBack={goBack} />;
      case 'results':
        return <ResultsScreen
          onNavigate={(screen) => {
            // Для навигации из результатов очищаем стек
            if (screen === 'preparation' || screen === 'marathon') {
              console.log('🎯 App: Navigating from results to', screen, '- clearing stack');
              setIsTransitioning(true);
              setTimeout(() => {
                const newStack: Screen[] = ['main'];
                setScreenStack(newStack);
                setCurrentScreen(screen);

                // Сохраняем состояние экрана
                saveScreenState(screen, newStack);

                setTimeout(() => {
                  // Сброс скролла на всех уровнях
                  window.scrollTo(0, 0);
                  document.documentElement.scrollTop = 0;
                  document.body.scrollTop = 0;

                  const scrollableContainer = document.querySelector('.app-content-scrollable');
                  if (scrollableContainer) {
                    scrollableContainer.scrollTop = 0;
                  }

                  document.querySelectorAll('.overflow-y-auto, .app-content-scrollable').forEach(el => {
                    (el as HTMLElement).scrollTop = 0;
                  });
                }, 50);

                setIsTransitioning(false);
              }, 75);
            } else {
              navigateTo(screen);
            }
          }}
          onBack={() => {
            console.log('🎯 App: onBack from results - going to main');
            // При возврате из результатов всегда идем на главный экран
            goToMain();
          }}
          onRestart={() => {
            console.log('🎯 App: Restarting test - clearing stack');
            // Очищаем стек перед повторным запуском чего
            // Чтобы кнопка "Назад" из теста вела на главный экран
            setIsTransitioning(true);
            setTimeout(() => {
              const newStack: Screen[] = ['main'];
              setScreenStack(newStack);
              setCurrentScreen('test');

              // Сохраняем состояние экрана
              saveScreenState('test', newStack);

              setTimeout(() => {
                // Сброс скролла на всех уровнях
                window.scrollTo(0, 0);
                document.documentElement.scrollTop = 0;
                document.body.scrollTop = 0;

                const scrollableContainer = document.querySelector('.app-content-scrollable');
                if (scrollableContainer) {
                  scrollableContainer.scrollTop = 0;
                }

                document.querySelectorAll('.overflow-y-auto, .app-content-scrollable').forEach(el => {
                  (el as HTMLElement).scrollTop = 0;
                });
              }, 50);

              setIsTransitioning(false);
            }, 75);
          }}
          onGoToMarathon={() => {
            console.log('🎯 App: onGoToMarathon called', {
              currentScreen,
              currentStack: [...screenStack]
            });
            // Очищаем стек и устанавливаем marathon как если бы пришли с главного экрана
            // При нажатии "Назад" из марафонов вернется на главный экран
            setIsTransitioning(true);
            setTimeout(() => {
              console.log('🎯 Setting stack to [main] and screen to marathon');
              const newStack: Screen[] = ['main']; // Только main в стеке
              setScreenStack(newStack);
              setCurrentScreen('marathon');
              console.log('🎯 Stack should now be: ["main"], screen: marathon');

              // Сохраняем состояние экрана
              saveScreenState('marathon', newStack);

              setTimeout(() => {
                // Сброс скролла на всех уровнях
                window.scrollTo(0, 0);
                document.documentElement.scrollTop = 0;
                document.body.scrollTop = 0;

                const scrollableContainer = document.querySelector('.app-content-scrollable');
                if (scrollableContainer) {
                  scrollableContainer.scrollTop = 0;
                }

                document.querySelectorAll('.overflow-y-auto, .app-content-scrollable').forEach(el => {
                  (el as HTMLElement).scrollTop = 0;
                });
              }, 50);

              setIsTransitioning(false);
            }, 75);
          }}
        />;
      case 'review':
        return <AnswerReviewScreen onNavigate={navigateTo} onBack={goBack} />;
      case 'statistics':
        return <StatisticsScreen onNavigate={navigateTo} onBack={goBack} />;
      case 'error-top':
        return <ErrorTopScreen onNavigate={navigateTo} onBack={goBack} />;
      case 'useful-info':
        return <UsefulInfoScreen onNavigate={navigateTo} onBack={goBack} />;
      case 'useful-links':
        return <UsefulLinksScreen onNavigate={navigateTo} onBack={goBack} />;
      case 'document-view':
        return <DocumentViewScreen onNavigate={navigateTo} onBack={goBack} />;
      case 'search':
        return <SearchScreen onNavigate={navigateTo} onBack={goBack} />;
      case 'preparation-help':
        return <PreparationHelpScreen onNavigate={navigateTo} onBack={goBack} onReplaceWithScreen={replaceWithScreen} />;
      case 'exam-help':
        return <ExamHelpScreen onNavigate={navigateTo} onBack={goBack} onReplaceWithScreen={replaceWithScreen} />;
      case 'marathon-help':
        return <MarathonHelpScreen onNavigate={navigateTo} onBack={goBack} onReplaceWithScreen={replaceWithScreen} />;
      case 'favorites-help':
        return <FavoritesHelpScreen onNavigate={navigateTo} onBack={goBack} onReplaceWithScreen={replaceWithScreen} />;
      case 'statistics-help':
        return <StatisticsHelpScreen onNavigate={navigateTo} onBack={goBack} onReplaceWithScreen={replaceWithScreen} />;
      case 'error-top-help':
        return <ErrorTopHelpScreen onNavigate={navigateTo} onBack={goBack} onReplaceWithScreen={replaceWithScreen} />;
      case 'document-help':
        return <DocumentHelpScreen onNavigate={navigateTo} onBack={goBack} onReplaceWithScreen={replaceWithScreen} />;
      case 'privacy':
        return <PrivacyScreen onNavigate={navigateTo} onBack={goBack} />;
      case 'terms':
        return <TermsScreen onNavigate={navigateTo} onBack={goBack} />;
      case 'contacts':
        return <ContactsScreen onNavigate={navigateTo} onBack={goBack} />;
      case 'about':
        return <AboutScreen onNavigate={navigateTo} onBack={goBack} />;
      case '404':
        return <Error404Screen onBackToMain={goToMain} />;

      // SEO Document Pages
      case 'documents-catalog':
        return <DocumentsCatalogPage onNavigate={navigateTo} onBack={goBack} />;
      case 'doc-77fz':
        return <DocumentDetailPage documentType="doc-77fz" onNavigate={navigateTo} onBack={goBack} />;
      case 'doc-weapons':
        return <DocumentDetailPage documentType="doc-weapons" onNavigate={navigateTo} onBack={goBack} />;
      case 'doc-periodic-check':
        return <DocumentDetailPage documentType="doc-periodic-check" onNavigate={navigateTo} onBack={goBack} />;
      case 'doc-criminal-code':
        return <DocumentDetailPage documentType="doc-criminal-code" onNavigate={navigateTo} onBack={goBack} />;
      case 'useful-links-page':
        return <LinksPage onBack={goBack} />;

      default:
        return <MainScreen onNavigate={navigateTo} />;
    }
  };

  return (
    <div className={`w-full min-h-full flex flex-col flex-1 min-h-0 ${getBackgroundClass()} text-foreground safe-area-left safe-area-right`}>
      <main
        className={`flex-1 min-h-0 flex flex-col w-full h-full transition-opacity duration-150 ${isTransitioning ? 'opacity-50' : 'opacity-100'}`}
        style={{ transition: 'opacity 150ms ease-in-out' }}
      >
        <Suspense fallback={null}>
          {renderScreen()}
        </Suspense>
      </main>
      <Suspense fallback={null}>
        <CookieConsent />
      </Suspense>
      <Suspense fallback={null}>
        <UpdateNotice />
      </Suspense>
      {currentScreen !== 'test' && (
        <YandexRTB type="floorAd" idMobile="R-A-17579918-12" idDesktop="R-A-17579918-3" />
      )}
    </div>
  );
};

export default function App() {
  return (
    <ErrorBoundary>
      <AppProvider>
        <AppInner />
      </AppProvider>
    </ErrorBoundary>
  );
}
